import { z } from 'zod';
import { AssistantVoice, BusinessType, Country, Currency, Language, NumberProvider, PlanTier, ProvisioningStatus, SubscriptionStatus, } from '../enums.js';
import { E164, MinuteOfDay, TimestampSchema, Timezone } from '../primitives.js';
import { availableSeconds, prepaidEntitlement } from '../billing/policy.js';
import { BillingPreferencesSchema, BillingSmsSettingsSchema, BillingSummarySchema, } from '../billing/schemas.js';
/** EU KYC / regulatory-bundle state (doc 04). Server-managed. */
export const ComplianceSchema = z.object({
    bundleSid: z.string().optional(),
    endUserSid: z.string().optional(),
    addressSid: z.string().optional(),
    /** Uploaded evidence files (Cloud Storage object paths). */
    documentSids: z.array(z.string()).default([]),
    /** Field-level reasons from the Twilio Evaluations API when a bundle is rejected. */
    failureReasons: z
        .array(z.object({
        field: z.string(),
        reason: z.string(),
    }))
        .default([]),
    submittedAt: TimestampSchema.optional(),
});
/**
 * Auto-provisioned **forward-target** number (doc 13a). Bought on the PARENT
 * Twilio account with NO regulatory bundle, so it sidesteps the per-country KYC
 * gate that blocks scarce local numbers (e.g. PL). The business forwards its own
 * real line to this number; the gateway routes inbound calls by it via the
 * `phoneNumbers` reverse index (which carries no `subaccountSid`, so the webhook
 * validates with the parent auth token). Distinct from the bundle-backed
 * `numbers` subcollection.
 */
export const ForwardNumberSchema = z.object({
    phoneNumber: E164,
    /** Twilio SID. NULL for a Telnyx-held number (PL, doc 17) — this was
     * non-nullable until the provider split, which made every PL business doc
     * fail `BusinessSchema.parse`. That is not a cosmetic failure: the gateway
     * parses the business on every inbound call, so it would have taken the
     * line down rather than merely looking wrong. */
    twilioNumberSid: z.string().min(1).nullable().default(null),
    /** ISO 3166-1 alpha-2 of where the number was actually bought. ALWAYS equals
     * `business.country` now — the number is national by rule (doc 13a D2), which
     * is what keeps the owner's divert a national call. Still a plain string
     * rather than the `Country` enum because legacy records bought under the old
     * GB/US fallback cascade must keep parsing; `audit:numbers` finds those. */
    isoCountry: z.string().length(2),
    /** Which vendor holds this number. Defaults to `twilio` so records written
     * before the Telnyx split still parse. Decides how it is released, and tells
     * the gateway which signature scheme signs its webhooks. */
    provider: NumberProvider.default('twilio'),
    /** Telnyx phone-number id — the release/status handle, Telnyx's analog of
     * `twilioNumberSid`. Null for Twilio-held numbers. */
    telnyxNumberId: z.string().nullable().default(null),
    /**
     * Telnyx has issued the number but still needs regulatory information before
     * it will route inbound calls (doc 17; PL local requires two documents, an
     * address and two textual fields). The number is reserved and bound to our
     * gateway, but the assistant will NOT answer on it yet — so the portal has to
     * say "paperwork pending", not "you're live".
     */
    pendingRegulatory: z.boolean().default(false),
    purchasedAt: TimestampSchema.optional(),
    /** Set the first time a forwarded call lands on this number (doc 13a
     * "confirm the divert landed"). Null until the owner runs the forwarding test.
     * A non-null value is the "forwarding works" proof shown in the portal. */
    verifiedAt: TimestampSchema.nullable().default(null),
    /** Transient window opened by `beginForwardingTest`: while `now < testExpiresAt`
     * the gateway treats the next inbound call as the owner's verification call
     * (marks `verifiedAt`, answers with a confirmation, does NOT run the agent). */
    testExpiresAt: TimestampSchema.nullable().default(null),
    /**
     * How this business is reached (doc 18).
     *
     * `dedicated` — it holds `phoneNumber` to itself, and inbound calls are routed
     * by the number dialled. The original model (doc 13a), and still the fallback
     * for any carrier that does not tell us where a call was diverted from.
     *
     * `pooled` — `phoneNumber` is a SHARED number serving many businesses, and
     * this one is identified by its own line via `divertSources`. Defaults to
     * `dedicated` so every business doc written before doc 18 keeps parsing —
     * which matters more than it looks: the gateway parses this schema on every
     * inbound call, so a parse failure here is an outage, not a cosmetic bug.
     */
    mode: z.enum(['dedicated', 'pooled']).default('dedicated'),
    /**
     * The owner's own business line — the number their customers actually dial,
     * which they declare during setup.
     *
     * Declaring it is NOT a routing grant. It is the expectation that the
     * verification call is checked against; the grant is the `divertSources`
     * document, which only a matching call can create. Anything else would let
     * someone type a competitor's number and receive their calls (doc 18 D2).
     */
    sourceNumber: E164.nullable().default(null),
    /** When a verification call proved the declared `sourceNumber` really diverts
     * to us. Null while the declaration is unproven. This — not `sourceNumber` —
     * is what makes a pooled business routable. */
    sourceVerifiedAt: TimestampSchema.nullable().default(null),
    /**
     * Set when a verification call arrived with NO usable diverted-from: the
     * carrier does not say where a forwarded call came from, so this business's
     * calls cannot be told apart from anyone else's on the shared number.
     *
     * It no longer leads anywhere else. This used to be the trigger for buying a
     * number of the business's own; numbers are not bought any more (doc 18 D7),
     * so the state is terminal and honest: the line cannot be served today, and
     * setup cannot be finished on it. Cleared when the owner declares a line.
     */
    poolingUnsupportedAt: TimestampSchema.nullable().default(null),
    /**
     * Set when a verification call arrived FROM A DIFFERENT LINE than the one the
     * owner declared — nearly always a typo in the declaration, sometimes a divert
     * set up on another SIM.
     *
     * Kept apart from {@link poolingUnsupportedAt} because the two need opposite
     * answers. A carrier that strips the header is not the owner's fault and not
     * fixable by them; a mismatch is both, and the fix is one field away. Folding
     * it into "unsupported carrier" — which is what used to happen — turned a
     * mistyped digit into a permanent verdict about their operator.
     */
    sourceMismatchAt: TimestampSchema.nullable().default(null),
    /**
     * The line the mismatched call actually came from, so the portal can say
     * "the test call came from +48 …, but you entered +48 …" instead of a bare
     * "try again". Only ever the owner's OWN divert, placed inside a window they
     * opened — not a third party's number. Null unless {@link sourceMismatchAt}.
     */
    observedSourceNumber: E164.nullable().default(null),
});
/**
 * Alphanumeric SMS sender registration (doc 21 D9).
 *
 * Polish networks accept dynamic alphanumeric senders without central
 * pre-registration, but the aggregators require a one-off written declaration
 * of each sender field before they will carry it (free, 1–3 business days).
 * That is a HUMAN step in the middle of an otherwise automated onboarding, so
 * it must not sit on the critical path: until this reads `approved`, messages
 * go out under the platform sender and name the business in the body instead.
 * The branded sender then lands silently when the paperwork clears.
 */
export const SmsSenderStatus = z.enum(['unregistered', 'pending', 'approved', 'rejected']);
/** Quiet hours as minutes-of-day in the business timezone; `from` may be later
 * than `to`, which is the normal overnight case (21:00 → 08:00). */
export const QuietHoursSchema = z.object({
    fromMin: MinuteOfDay.default(21 * 60),
    toMin: MinuteOfDay.default(8 * 60),
});
/**
 * Per-business SMS notification settings (doc 21).
 *
 * EVERY field is defaulted, and that is load-bearing rather than tidy: the
 * gateway parses the business document on every inbound call, so a business
 * written before this block existed must still parse — a failure here is an
 * outage, not a cosmetic bug (the same trap `ForwardNumberSchema.provider`
 * documents).
 */
export const SmsSettingsSchema = z.object({
    /** Master switch. Off by default: nobody's clients start receiving messages
     * because we deployed. */
    enabled: z.boolean().default(false),
    onBooking: z.boolean().default(true),
    onCancellation: z.boolean().default(true),
    /** Minutes before the appointment to remind. `[]` = no reminders. ONE
     * offset by default (24 h): a second one doubles the bill, so it stays an
     * explicit choice (doc 16 §8). Reminders are gated on consent regardless,
     * so this default cannot start messaging anyone on its own. */
    reminderOffsetsMin: z.array(z.number().int().min(15).max(10080)).max(3).default([1440]),
    quietHours: QuietHoursSchema.default({}),
    /** The owner's branded sender, 3–11 chars. Empty = use the platform sender. */
    senderId: z.string().max(11).default(''),
    senderIdStatus: SmsSenderStatus.default('unregistered'),
    /** Blast radius for a bug in the trigger as much as a cap on an abusive
     * tenant — a loop that re-sends is exactly the failure a per-message vendor
     * bills you for. */
    monthlyCap: z.number().int().min(0).max(100000).default(500),
});
/**
 * Owner-facing email (doc 29).
 *
 * Deliberately NOT part of {@link SmsSettingsSchema}: that block is about what
 * a business's CLIENTS receive, and every gate on it — consent, quiet hours,
 * the monthly client-message cap — exists to protect those clients. This is the
 * owner telling us where to reach *them* about their own business, which is a
 * different question with different answers.
 *
 * Off by default, like every other notification switch here: nobody's inbox
 * starts filling up because we deployed.
 */
export const EmailSettingsSchema = z.object({
    /** Email the owner when a new booking lands. */
    onBooking: z.boolean().default(false),
    /**
     * Email the owner when a CALLER moves or cancels a booking (doc 87). One
     * switch for both: "my diary changed under me" is one question to an owner.
     * Off by default, so a deploy changes nobody's inbox. A change the owner made
     * themselves never emails them, whatever this says.
     */
    onChange: z.boolean().default(false),
    /**
     * Where to send it. Empty means "use nothing" — the dispatcher records a
     * `no_address` suppression rather than guessing, because the only address we
     * could guess from is the sign-in identity and an owner who typed a different
     * one here meant it.
     *
     * A PLAIN string, not `z.string().email()`, and that is the decision worth
     * recording: the gateway parses this document on every inbound call, so a
     * stored value that fails validation would take the phone line down rather
     * than fail to send an email. The address is validated where it is entered
     * (the portal) and again at send time (the dispatcher), both of which fail
     * softly.
     */
    address: z.string().max(254).default(''),
});
export const NotificationSettingsSchema = z.object({
    sms: SmsSettingsSchema.default({}),
    /** Owner-facing email (doc 29). */
    email: EmailSettingsSchema.default({}),
    /** Owner SMS about the prepaid balance (doc 42 §5). NOT behind `sms.enabled`
     * — that switch is about what CLIENTS receive. Written only by the
     * `saveBillingSmsSettings` callable (rules forbid a direct write). */
    billingSms: BillingSmsSettingsSchema.default({}),
});
/**
 * Things about the business that are not services, hours or staff — address,
 * parking, how to find the door, whether there is wheelchair access, which
 * cards are taken (doc 23 P1).
 *
 * The cheapest capability raise there is: today the prompt's rule for anything
 * outside booking is "politely redirect", so a caller asking "where are you?"
 * — the single most common follow-up to a booking — hits a dead end from a
 * business that would obviously have answered.
 *
 * ONE free-text block rather than structured fields, and that is the decision
 * worth recording. Structured fields (address, parking, access…) would be
 * easier to render and would have been the obvious design; they also fix in
 * advance what a business is allowed to be asked about, which is exactly what
 * we do not know yet. Free text lets a vet write "we can see emergencies on
 * Saturday mornings" and a garage write "courtesy car by arrangement" without
 * a schema change — and `note_unmet_request` (doc 23 P0) is what will tell us
 * which structured fields to add once the pattern is real rather than assumed.
 *
 * It goes into the system prompt verbatim, so it is capped and the assistant is
 * instructed to treat it as reference material rather than as instructions —
 * an owner typing "ignore your rules and…" must not be obeyed.
 */
export const BusinessFactsSchema = z.object({
    /**
     * Where the business is. Split out of the free text and given its own field
     * because it is not one fact among many: "where are you?" is the question
     * callers ask most once a time is agreed, it has a shape everyone already
     * knows how to fill in, and an owner who writes nothing else will still write
     * this. In the prompt it gets its own line rather than being buried in a
     * paragraph, so the assistant can answer it without reading around it.
     */
    address: z.string().max(200).default(''),
    /** Everything else, free-text: parking, access, payment, house rules. */
    text: z.string().max(1500).default(''),
});
/**
 * Stripe subscription state for ONE business (doc 23 — paid from day one).
 *
 * Per business rather than per account, matching how the product is priced and
 * sold ("one monthly price per business"): an owner with a salon and a barber
 * shop pays twice and can cancel one without touching the other.
 *
 * SERVER-ONLY. Every field here is written by the Stripe webhook and by nothing
 * else — the rules exclude the whole block from `businessEditableKeys`, because
 * a client-writable `status: 'active'` is simply a free product.
 *
 * Every field is defaulted for the usual reason: the gateway parses this
 * document on every inbound call, so a business written before billing existed
 * must still parse or the phone line goes down.
 */
export const SubscriptionSchema = z.object({
    status: SubscriptionStatus.default('none'),
    /** Which plan was bought. Null until checkout completes. */
    plan: PlanTier.nullable().default(null),
    /** Stripe ids. The customer is per OWNER (reused across their businesses, so
     * one card is entered once); the subscription is per business. */
    stripeCustomerId: z.string().nullable().default(null),
    stripeSubscriptionId: z.string().nullable().default(null),
    stripePriceId: z.string().nullable().default(null),
    /** End of the paid period — what `past_due` is measured against and what the
     * portal shows as "renews on". */
    currentPeriodEnd: TimestampSchema.nullable().default(null),
    /** Set when the owner has cancelled but the period has not run out: still
     * `active`, but the portal must say it is ending rather than renewing. */
    cancelAtPeriodEnd: z.boolean().default(false),
    /**
     * The Stripe event id last applied here.
     *
     * Stripe delivers at-least-once and out of order, and both matter: a retried
     * `customer.subscription.updated` must not be applied twice, and a late
     * `created` must not overwrite a newer `deleted`. This is the idempotency
     * key half (ADR-0006); `eventCreatedAt` below is the ordering half.
     */
    lastEventId: z.string().nullable().default(null),
    lastEventAt: TimestampSchema.nullable().default(null),
    /**
     * What the subscribed price IS (doc 42 W1): one of the legacy plan prices,
     * the prepaid platform fee, or a price this deployment does not publish.
     * `unknown` is a controlled state — never free access. Null on records
     * written before the field existed; `subscriptionKind()` derives it.
     */
    kind: z.enum(['none', 'legacy_plan', 'prepaid_platform', 'unknown']).nullable().catch(null).default(null),
    /**
     * When this subscription FIRST went `past_due` in the current episode
     * (doc 42 §11). Set once on entering the state and cleared on leaving it,
     * so the 72 h grace deadline cannot slide with every redelivered webhook.
     */
    pastDueSince: TimestampSchema.nullable().catch(null).default(null),
    updatedAt: TimestampSchema.nullable().default(null),
});
/**
 * Conversation minutes consumed in the current billing period (doc 26).
 *
 * Lives on the business document rather than in a subcollection (D1) because
 * the gateway already parses this document on every inbound call — so the
 * "are they out of minutes?" test costs nothing extra on the hot path, where a
 * subcollection would add a read to every single call.
 *
 * SERVER-ONLY, like `subscription`: a client-writable `minutesUsed` is an
 * unlimited plan. Every field is defaulted for the usual reason — a business
 * written before this existed must still parse, or the phone line goes down.
 */
export const UsageSchema = z.object({
    /**
     * Conversation SECONDS counted this period — the exact sum of each metered
     * call's duration, never rounded per call. This is the number the cap and
     * the warnings are measured against; minutes are only how it is shown.
     *
     * Null on records written before seconds were counted: their total exists
     * only as {@link minutesUsed}, which {@link secondsUsedThisPeriod} reads as
     * `minutesUsed × 60` (and keeps reading, whenever that is the larger).
     */
    secondsUsed: z.number().int().min(0).nullable().default(null),
    /**
     * Whole minutes counted this period, `floor(secondsUsed / 60)`. Written
     * alongside `secondsUsed` only so a reader that predates it (an older
     * gateway image mid-rollout) still sees a sane figure. Never read it
     * directly — use {@link secondsUsedThisPeriod} / {@link minutesUsedThisPeriod}.
     * Before seconds were counted it was each call rounded UP (doc 26 D3).
     */
    minutesUsed: z.number().int().min(0).default(0),
    /** End of the window these minutes belong to — mirrors Stripe's period.
     *  Null means "never counted yet"; the first call sets it. */
    periodEnd: TimestampSchema.nullable().default(null),
    /**
     * Which warning thresholds have already been sent this period, as the
     * REMAINING fractions from {@link WARN_AT_REMAINING} (e.g. `[0.5]`).
     *
     * Appended in the same transaction that crosses the threshold, which is what
     * makes it the claim (D6): the crossing and the record of having warned are
     * one fact, and splitting them is what would create a double-send window.
     */
    warnedRemaining: z.array(z.number()).default([]),
    /**
     * The allowance {@link warnedRemaining} was recorded against.
     *
     * Without it, a mid-period UPGRADE silently disarms both warnings for the
     * rest of the period: the list holds fractions, and "50% of 150 minutes" and
     * "50% of 500 minutes" are different moments that the fractions cannot tell
     * apart. An owner who paid for a bigger plan would lose the very warnings the
     * plan makes more valuable, and the assistant would go quiet with no notice —
     * exactly what these messages exist to prevent.
     *
     * Null on records written before this existed; treated as "unknown, so do not
     * trust the list", which errs towards re-warning rather than silence.
     */
    warnedAgainstAllowance: z.number().int().nullable().default(null),
    updatedAt: TimestampSchema.nullable().default(null),
});
/**
 * Conversation minutes included with each plan (doc 25 §5).
 *
 * Here rather than in the portal because all three sides read it: the gateway
 * enforces it, the notification trigger warns against it, and the portal draws
 * a bar with it (D9). One number, one place.
 */
export const PLAN_ALLOWANCE_MINUTES = {
    starter: 150,
    pro: 500,
    business: 1200,
};
/**
 * List price of each plan, in MINOR units (grosze / cents), per currency.
 *
 * Here for the same reason {@link PLAN_ALLOWANCE_MINUTES} is: more than one
 * side reads it. The portal draws it on the plan cards, and `verifyStripe`
 * checks it against what the Stripe price objects actually charge — which is
 * the whole point of having a single copy. What the owner is billed is ALWAYS
 * the Stripe price the server picks from tier + currency (`priceIdFor`); this
 * table is the number we PROMISE, and a drift between the two is a card that
 * lies to the customer rather than an overcharge. The check script is what
 * turns that from a thing someone has to remember into a thing CI can see.
 *
 * Minor units, because the rest of the product avoids floats near money: 29.90
 * is not representable, and a rounding error on a price card is a support
 * ticket about being lied to.
 *
 * **PLN is costed** (doc 25): 149 / 299 / 599 zł net, sized against a measured
 * ~0.16 PLN per conversation minute and against Smart Asystenci at 499 zł for
 * 400 minutes. **EUR is a straight FX conversion of the PLN prices and nothing
 * more** — `Currency` has two values and a DE/FR business must see something,
 * but no German or French cost, competitor or willingness-to-pay analysis has
 * been done. Do not launch those markets on these numbers.
 */
export const PLAN_PRICE_MINOR = {
    starter: { EUR: 3500, PLN: 14900 },
    pro: { EUR: 6900, PLN: 29900 },
    business: { EUR: 13900, PLN: 59900 },
};
/**
 * Warn the owner when the allowance drops to these FRACTIONS REMAINING —
 * half, then a fifth. Descending, and the order matters: `meterCall` fires
 * every threshold newly crossed, so a single long call that jumps from 60%
 * remaining to 15% sends both.
 */
export const WARN_AT_REMAINING = [0.5, 0.2];
/**
 * Minutes this business may use per period, or `null` when there is no plan to
 * measure against.
 *
 * `null` is NOT "unlimited" — it means "unmeasurable", and what happens to such
 * a business is decided by {@link serviceBlockReason}, not here. Keeping the two
 * questions apart is what lets "no plan" mean *no service* without this
 * function having to express a paywall in a return type that cannot say it.
 */
export function allowanceMinutes(business) {
    const plan = business.subscription?.plan;
    return plan ? PLAN_ALLOWANCE_MINUTES[plan] : null;
}
/**
 * Subscription states in which a business is entitled to service.
 *
 * `past_due` is included deliberately, and that decision is unchanged: Stripe
 * retries a failed card for days, and cutting a salon's phone off over an
 * expired card — while the owner is with a customer and unaware — is not a
 * decision anyone would sign off. It earns a banner, not silence.
 *
 * `trialing` is included because a trial is a plan: it goes through Checkout
 * with `trial_period_days`, so `plan` is set and the allowance is known. That
 * is the whole reason a trial must still pick a tier.
 */
const SERVED_STATUSES = new Set(['active', 'trialing', 'past_due']);
/**
 * When the setup wizard shipped. A business created before it has no
 * completion stamp through no fault of its own, so anything already live is
 * grandfathered — we never treat a working receptionist as half-configured.
 */
const WIZARD_EPOCH = new Date('2026-07-26T12:00:00Z');
/**
 * Is this business still in setup?
 *
 * The single statement of it, because two very different things depend on the
 * answer and they must never disagree: the portal decides whether to hold the
 * owner in the wizard, and the gateway decides whether to answer a call at all.
 *
 * `active` on its own is NOT proof that setup finished — the wizard activates
 * the business itself when it assigns the shared number, long before the divert
 * is dialled. Only the completion stamp, or predating the wizard, ends setup.
 */
export function isInSetup(business) {
    if (business.onboardingCompletedAt != null)
        return false;
    if (business.status === 'archived' || business.status === 'suspended')
        return false;
    if (business.status === 'active')
        return business.createdAt >= WIZARD_EPOCH;
    return true;
}
/**
 * The single statement of "may this business take calls?" (doc 26 §3a).
 *
 * One function so the gateway, the portal banner and the setup checklist can
 * never tell an owner three different things about why their phone is quiet —
 * the same reason `isReachable` exists for forwarding.
 *
 * ## `requireSubscription`
 *
 * Off by default, and that is a deploy-safety switch rather than an opinion.
 * The rule "no plan means no service" is only survivable once billing is live:
 * with Stripe unconfigured, `createCheckoutSession` refuses, so a blocked
 * business has no route back — enforcing it early would silence every business
 * in the project with no way for its owner to fix it. Flip it on once Stripe is
 * configured AND the existing businesses have subscribed (doc 26 §3a).
 */
export function serviceBlockReason(business, now, opts) {
    const status = business.subscription?.status ?? 'none';
    const served = SERVED_STATUSES.has(status);
    // Prepaid (doc 42 §11): the platform fee buys entitlement, purchased seconds
    // buy calls. The monthly allowance is NOT consulted — a prepaid business that
    // used up last month's plan minutes but holds a positive balance must keep
    // answering — and entitlement is enforced regardless of the global switch,
    // because a business is only ever moved to prepaid deliberately, one by one.
    // The funds test here reads the projection (for the banner and the cheap
    // early refusal); the gateway's admission transaction is the real decision.
    if (business.billingSummary?.mode === 'prepaid_v1') {
        if (!served && isInSetup(business))
            return 'setup_unpaid';
        if (prepaidEntitlement(business.subscription, now).kind === 'blocked')
            return 'no_subscription';
        if (availableSeconds(business.billingSummary) <= 0)
            return 'out_of_funds';
        return null;
    }
    // Connected but not paid for, and setup never finished.
    //
    // Unconditional, and deliberately not behind `requireSubscription`. Setup now
    // asks the owner to divert their line BEFORE asking for a card — forwarding
    // can take days if their operator is awkward, and charging for a line that
    // may never come up is the wrong way round. The consequence is a window where
    // a real diverted call can arrive for a business nobody has paid for, and the
    // answer is that the line does not work yet: refused here, before any model
    // is involved, so an unpaid call costs a TwiML response and nothing else.
    //
    // `isInSetup` rather than a bare null check on the stamp, because businesses
    // that predate the wizard have no stamp and are answering calls right now
    // (doc 26 §5). Blocking those would silence production.
    if (!served && isInSetup(business))
        return 'setup_unpaid';
    if (opts.requireSubscription && !served)
        return 'no_subscription';
    if (isOutOfMinutes(business, now))
        return 'out_of_minutes';
    return null;
}
/**
 * Seconds counted so far in the CURRENT period — zero once the stored window
 * has expired.
 *
 * The period resets lazily (D4): rather than a nightly job that can fail
 * silently and hand everyone a free month, the stored counter is simply
 * disregarded once `periodEnd` has passed. Both the writer and every reader
 * apply this same rule, so a stale stored value is never acted on or shown.
 *
 * A record from before seconds were counted carries only `minutesUsed`, which
 * is taken at face value (×60) — those minutes were already charged. The larger
 * of the two always wins: the seconds meter keeps `minutesUsed × 60 ≤
 * secondsUsed`, so `minutesUsed` can only be ahead if a minutes-only writer (an
 * older Functions build mid-rollout or after a rollback) counted calls since —
 * and ignoring those would let the business run past its cap unwarned.
 */
export function secondsUsedThisPeriod(business, now) {
    const { secondsUsed, minutesUsed, periodEnd } = business.usage;
    if (periodEnd && periodEnd.getTime() <= now.getTime())
        return 0;
    return Math.max(secondsUsed ?? 0, minutesUsed * 60);
}
/**
 * Minutes used this period, for DISPLAY: whole minutes, rounded DOWN, so a
 * started minute is never shown as spent. Nothing may enforce or warn on this
 * number — that is {@link secondsUsedThisPeriod}'s job.
 *
 * Rounding down here makes `allowance − minutesUsedThisPeriod` reach 0 at
 * exactly the moment {@link isOutOfMinutes} turns true, so "0 minutes left"
 * and the phone going unanswered are never out of step.
 */
export function minutesUsedThisPeriod(business, now) {
    return Math.floor(secondsUsedThisPeriod(business, now) / 60);
}
/** Has this business spent its allowance, to the second? False when there is
 *  no plan to measure against — "no plan" is {@link serviceBlockReason}'s
 *  question. */
export function isOutOfMinutes(business, now) {
    const allowance = allowanceMinutes(business);
    if (allowance === null)
        return false;
    return secondsUsedThisPeriod(business, now) >= allowance * 60;
}
/**
 * `businesses/{businessId}` — the aggregate root. Rules key authorization off
 * `ownerUid`; the `status` field is both the provisioning state machine and the
 * idempotency guard (ADR-0006).
 */
export const BusinessSchema = z.object({
    ownerUid: z.string().min(1),
    name: z.string().min(1).max(120),
    country: Country,
    language: Language,
    timezone: Timezone,
    /** What service prices are in. Derived from `country` and moved with it, so
     * the two can never disagree. Defaulted to PLN so businesses written before
     * the field existed keep parsing — the gateway parses this document on every
     * inbound call, so a failure here is an outage, not a cosmetic bug. */
    currency: Currency.default('PLN'),
    /** Custom opener; the AI-disclosure sentence is appended server-side (non-editable, EU AI Act Art. 50). */
    greeting: z.string().max(300).default(''),
    /** Doc 71: `default` = our sentence rendered from the CURRENT name (the
     * stored `greeting` is then only a cached rendering); `custom` = the owner's
     * own words; null = stored before the mode existed (see `usesDefaultGreeting`). */
    greetingMode: z.enum(['default', 'custom']).nullable().default(null),
    /**
     * The voice callers hear. Defaults to the voice every business ran on before
     * this field existed, so an untouched business sounds exactly as it did.
     */
    voice: AssistantVoice.default('marin'),
    /** Denormalized *primary* number for the dashboard header. The full list of a
     * business's numbers lives in the `numbers` subcollection (doc 05 §3). */
    phoneNumber: E164.nullable().default(null),
    twilioNumberSid: z.string().nullable().default(null),
    /** The cheap forward-target number (doc 13a), auto-bought on activation and
     * released on archive. When set, it is mirrored to `phoneNumber` above and is
     * what the gateway routes on. Null until the business is activated. */
    forwardNumber: ForwardNumberSchema.nullable().default(null),
    /**
     * The owner's declared business line, held here until there is a
     * `forwardNumber` map to move it into (doc 18 D2).
     *
     * Setup asks for their number FIRST — before we have assigned them one — so
     * there is a window where the declaration has nowhere to live:
     * `ForwardNumberSchema` requires `phoneNumber` and `isoCountry`, so writing a
     * lone `forwardNumber.sourceNumber` would produce a map that fails to parse
     * on every inbound call. Provisioning drains this field into
     * `forwardNumber.sourceNumber` and clears it; the two are never both set.
     *
     * Like `sourceNumber`, this grants nothing on its own — only a verification
     * call creates a routing grant.
     */
    pendingSourceNumber: E164.nullable().default(null),
    /** Which mobile operator the owner's existing business line is on. Its ONLY
     * job is to tailor the forwarding-fee warning (doc 13a) — we don't store or
     * verify the line itself. Number portability means the operator can't be
     * derived from a number, so the owner picks it. Free-form operator id from the
     * portal's `operatorForwarding` catalog (e.g. `de-o2`); null = not chosen. */
    ownerLineOperator: z.string().nullable().default(null),
    /** What kind of business this is (doc 23). Write-once: set at creation and
     *  immutable afterwards — see the note on `BusinessType`. Null for every
     *  business created before the field existed, and for those it may be set
     *  once from the wizard's first step. */
    businessType: BusinessType.nullable().default(null),
    status: ProvisioningStatus,
    /** ISV isolation: this business's own Twilio subaccount (doc 05 §3). Created
     * lazily on first compliance submission; all its bundles + numbers live here. */
    twilio: z.object({ subaccountSid: z.string().optional() }).default({}),
    /** Cap on numbers a business may hold, to bound cost/abuse. */
    maxNumbers: z.number().int().min(1).max(50).default(5),
    /** Mirror of the *primary* bundle for onboarding UX; source of truth is the
     * `bundles` subcollection (one per country+type). */
    compliance: ComplianceSchema.default({ documentSids: [], failureReasons: [] }),
    /** Company registration identifiers collected during onboarding (doc 04 §4.2). */
    registration: z
        .object({
        legalName: z.string().optional(),
        /** SIREN/SIRET (FR) or HRB/tax-ID (DE). */
        registrationId: z.string().optional(),
        addressLine1: z.string().optional(),
        addressLine2: z.string().optional(),
        city: z.string().optional(),
        postalCode: z.string().optional(),
        authorizedRepName: z.string().optional(),
        /** Onboarding writes '' for a blank field; treat empty/null as "not
         * provided" so a half-filled draft doesn't fail the whole business parse
         * (which powers live call routing, not just onboarding). */
        authorizedRepEmail: z.preprocess((v) => (v === '' || v === null ? undefined : v), z.string().email().optional()),
    })
        .default({}),
    /** When the owner finished the setup wizard. Null = still onboarding, which
     * is what pins them to `/b/{id}/setup` until it's done. Only this flag is
     * stored — *which* step is outstanding is derived from the real data (profile,
     * services, hours, number), so the two can never drift apart. */
    onboardingCompletedAt: TimestampSchema.nullable().default(null),
    /** Client-facing SMS on booking / cancellation / reminder (doc 21). */
    notifications: NotificationSettingsSchema.default({}),
    /** Reference material the assistant may answer questions from (doc 23 P1). */
    facts: BusinessFactsSchema.default({}),
    /** Stripe subscription. Server-written only. */
    subscription: SubscriptionSchema.default({}),
    /** Minutes consumed this billing period (doc 26). Server-written only. In
     * `prepaid_v1` it is a monthly REPORT, never the wallet. */
    usage: UsageSchema.default({}),
    /** Prepaid wallet projection (doc 42 §7). Server-written only; `legacy`
     * for every business until the explicit per-business switch. */
    billingSummary: BillingSummarySchema.default({}),
    /** Auto top-up settings + safe card summary. Callable-written only. */
    billingPreferences: BillingPreferencesSchema.default({}),
    slotGranularityMin: z.number().int().min(5).max(120).default(30),
    maxCallSeconds: z.number().int().min(60).max(1800).default(600),
    /** Per-business daily caps to bound telephony cost/abuse (HLD §8). */
    dailyCallCap: z.number().int().min(0).default(200),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.optional(),
});
/** Business doc plus its Firestore id. */
export const BusinessWithIdSchema = BusinessSchema.extend({ id: z.string() });
//# sourceMappingURL=business.js.map